import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import Modal from '../../common/Modal';
import FormControl from '../../common/FormControl';
import Button from '../../common/Button';
import Toast, { ToastMessage } from '../../common/Toast';
import { tasksService, Task, TaskUpdate, TaskStatus, TaskPriority } from '../../../services/tasksService';
import { useAuth } from '../../../contexts/AuthContext';
import extractApiError from '../../../utils/apiError';
import { useFieldValidation } from '../../../validation/useFieldValidation';
import { ValidatedInput, ValidatedTextarea, ValidatedDatePicker } from '../../../validation/ValidatedFields';

// ── Constants ────────────────────────────────────────────────────────────────

const TERMINAL_STATUSES: ReadonlyArray<string> = ['DONE', 'VERIFIED', 'REJECTED', 'CANCELLED', 'ARCHIVED'];

const PRIORITIES: TaskPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];

const taskStatusVariant = (s: TaskStatus): string => {
    switch (s) {
        case 'DONE':
        case 'VERIFIED':    return 'success';
        case 'IN_PROGRESS': return 'info';
        case 'REVERTED':
        case 'REJECTED':
        case 'CANCELLED':   return 'danger';
        case 'ARCHIVED':    return 'neutral';
        default:            return 'warning'; // OPEN
    }
};

// ── Props ────────────────────────────────────────────────────────────────────

export interface TaskDetailModalProps {
    isOpen: boolean;
    taskId: number | null;
    frozen: boolean;
    onClose: () => void;
    onChanged: () => void;
}

// ── Form shape (only fields TaskUpdate accepts) ──────────────────────────────

interface TaskForm {
    name: string;
    description: string;
    due_date: string;
    priority: TaskPriority | '';
}

const emptyForm = (): TaskForm => ({
    name: '',
    description: '',
    due_date: '',
    priority: '',
});

const taskToForm = (t: Task): TaskForm => ({
    name: t.name ?? '',
    description: t.description ?? '',
    due_date: t.due_date ?? '',
    priority: t.priority ?? '',
});

// ── Component ────────────────────────────────────────────────────────────────

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
    isOpen,
    taskId,
    frozen,
    onClose,
    onChanged,
}) => {
    const { state } = useAuth();
    const user = state.user;

    const [task, setTask] = useState<Task | null>(null);
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState<TaskForm>(emptyForm());
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const fieldVh = useFieldValidation();

    // Fetch task on open
    useEffect(() => {
        if (!isOpen || taskId == null) {
            setTask(null);
            setForm(emptyForm());
            fieldVh.reset();
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const t = await tasksService.get(taskId);
                if (!cancelled) {
                    setTask(t);
                    setForm(taskToForm(t));
                    fieldVh.reset();
                }
            } catch (e: any) {
                if (!cancelled) {
                    setToast({ type: 'error', message: extractApiError(e, 'Failed to load task') });
                    setTask(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taskId, isOpen]);

    // Editability
    const isOwner = !!user?.id && !!task && user.id === task.created_by;
    const isTerminal = !!task && TERMINAL_STATUSES.includes(task.status);
    const editable = !!task && isOwner && !frozen && !isTerminal;

    // View-only reason note
    const viewOnlyReason = (): string => {
        if (!task) return '';
        if (!isOwner) return 'View only — you are not the owner of this task.';
        if (frozen) return 'View only — investigation is locked or closed.';
        if (isTerminal) return `View only — task is in terminal status (${task.status}).`;
        return '';
    };

    const handleSave = async () => {
        if (!task || !editable) return;

        const valid = fieldVh.validateAll([
            { name: 'name', code: 109, value: form.name, required: true },
            { name: 'description', code: 110, value: form.description },
            { name: 'due_date', code: 305, value: form.due_date },
        ]);
        if (!valid) return;

        setSubmitting(true);
        try {
            const update: TaskUpdate = {
                name: form.name || undefined,
                description: form.description || undefined,
                due_date: form.due_date || null,
                priority: form.priority || null,
            };
            await tasksService.update(task.id, update);
            // No success toast here: onClose() unmounts this modal immediately,
            // so its own <Toast> would never render. The parent list reload +
            // the modal closing are the success feedback.
            onChanged();
            onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to update task') });
        } finally {
            setSubmitting(false);
        }
    };

    const headerTitle = task ? task.name : 'Task Detail';

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="md" data-testid="MOD_task_detail">
                <Modal.Header>{headerTitle}</Modal.Header>
                <Modal.Body>
                    {loading && (
                        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
                            Loading…
                        </div>
                    )}

                    {!loading && task && (
                        <>
                            {/* View-only notice */}
                            {!editable && viewOnlyReason() && (
                                <div
                                    style={{
                                        marginBottom: 16,
                                        padding: '8px 12px',
                                        background: '#f9fafb',
                                        borderRadius: 6,
                                        border: '1px solid #e5e7eb',
                                        fontSize: '0.8rem',
                                        color: '#6b7280',
                                    }}
                                    data-testid="MSG_task_detail_view_only"
                                >
                                    {viewOnlyReason()}
                                </div>
                            )}

                            {/* Status badge row */}
                            <div style={{ marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
                                <span className={`status-badge sm ${taskStatusVariant(task.status)}`}>
                                    {task.status.replace('_', ' ')}
                                </span>
                                {task.priority && (
                                    <span className="status-badge sm neutral">
                                        {task.priority}
                                    </span>
                                )}
                            </div>

                            {editable ? (
                                /* ── Editable form ── */
                                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                    <div className="col-span-2">
                                        <ValidatedInput
                                            vh={fieldVh}
                                            name="name"
                                            code={109}
                                            required={true}
                                            label="Name"
                                            value={form.name}
                                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                            placeholder="Task name"
                                            data-testid="INP_task_detail_name"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <ValidatedTextarea
                                            vh={fieldVh}
                                            name="description"
                                            code={110}
                                            label="Description"
                                            value={form.description}
                                            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                            placeholder="Describe the task"
                                            data-testid="INP_task_detail_description"
                                        />
                                    </div>
                                    <ValidatedDatePicker
                                        vh={fieldVh}
                                        name="due_date"
                                        code={305}
                                        label="Due Date"
                                        value={form.due_date}
                                        onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                                        data-testid="INP_task_detail_due_date"
                                    />
                                    <FormControl.SearchSelect
                                        label="Priority"
                                        options={PRIORITIES.map(p => ({ value: p, label: p }))}
                                        value={form.priority || null}
                                        onChange={v => setForm(f => ({ ...f, priority: (v as TaskPriority) ?? '' }))}
                                        clearable
                                        data-testid="INP_task_detail_priority"
                                    />
                                    {/* Read-only role info */}
                                    <div>
                                        <div className="form-label">Doer</div>
                                        <div data-testid="INP_task_detail_doer">
                                            {task.doer_name ?? (task.doer_id ? `#${task.doer_id}` : '—')}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="form-label">Approver</div>
                                        <div data-testid="INP_task_detail_approver">
                                            {task.approver_name ?? (task.approver_id ? `#${task.approver_id}` : '—')}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="form-label">Validator</div>
                                        <div data-testid="INP_task_detail_validator">
                                            {task.validator_name ?? (task.validator_id ? `#${task.validator_id}` : '—')}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* ── Read-only view ── */
                                <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                    <div className="col-span-2">
                                        <div className="form-label">Name</div>
                                        <div data-testid="INP_task_detail_name">{task.name}</div>
                                    </div>
                                    <div className="col-span-2">
                                        <div className="form-label">Description</div>
                                        <div data-testid="INP_task_detail_description">
                                            {task.description ?? '—'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="form-label">Due Date</div>
                                        <div data-testid="INP_task_detail_due_date">
                                            {task.due_date ?? '—'}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="form-label">Doer</div>
                                        <div data-testid="INP_task_detail_doer">
                                            {task.doer_name ?? (task.doer_id ? `#${task.doer_id}` : '—')}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="form-label">Approver</div>
                                        <div data-testid="INP_task_detail_approver">
                                            {task.approver_name ?? (task.approver_id ? `#${task.approver_id}` : '—')}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="form-label">Validator</div>
                                        <div data-testid="INP_task_detail_validator">
                                            {task.validator_name ?? (task.validator_id ? `#${task.validator_id}` : '—')}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {!loading && !task && !toast && (
                        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
                            No task selected.
                        </div>
                    )}
                </Modal.Body>

                {editable && (
                    <Modal.Footer>
                        <Button
                            variant="fill"
                            icon={Save}
                            disabled={submitting}
                            onClick={handleSave}
                            data-testid="BTN_task_detail_save"
                        >
                            {submitting ? 'Saving…' : 'Save'}
                        </Button>
                    </Modal.Footer>
                )}
            </Modal>
            <Toast toast={toast} onClose={() => setToast(null)} />
        </>
    );
};

export default TaskDetailModal;
